import { useState, useRef, useEffect, useCallback } from 'react';
import { Storage } from '../storage';
import { uid, formatDate, hasVisibleContent, getTimelinePreviewImage, stripImgTags, formatCompactCount } from '../utils';
import { nominatimSearch } from '../nominatim';
import { DEFAULT_COLORS, TIMELINE_CATEGORIES, TIMELINE_CATEGORY_GROUPS } from '../constants';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import RichTextEditor from '../components/RichTextEditor';
import NotificationBell from '../components/NotificationBell';
import TemplateBrowser from '../components/TemplateBrowser';
import AddToCollectionModal from '../components/AddToCollectionModal';
import MobileMenu from '../components/MobileMenu';
import FeedbackModal from '../components/FeedbackModal';

const CREATE_TAB_KEY = 'chronos_create_tab';

// ─── DASHBOARD PAGE ───────────────────────────────────────────────────────────
//
//  "My Timelines" — shows all of the current user's timelines as cards.
//  Lets the user create, view, edit, and delete timelines.
//
//  Props:
//    user      {object}    authenticated user
//    timelines {array}     array of timeline objects
//    onCreate  {function}  called with a new timeline object
//    onView    {function}  called with a timeline to view (read-only)
//    onEdit    {function}  called with a timeline to open in the editor
//    onDelete  {function}  called with the timeline id to delete
//    onLogout  {function}  signs the user out
//

const Dashboard = ({ user, timelines, sharedTimelines = [], onCreate, onView, onEdit, onDelete, onDuplicate, onLogout, onProfile, onFeed, onAdmin, onNotificationClick, onViewProfile, theme, onThemeToggle, autoOpenCreate, onAutoOpenCreateDone, canInstall, isStandalone, onInstall }) => {

  const [installNudgeDismissed, setInstallNudgeDismissed] = useState(
    () => !!localStorage.getItem('chronos_install_nudge_dismissed')
  );
  const showInstallNudge = canInstall && !isStandalone && !installNudgeDismissed && timelines.length >= 3;

  const [showNewForm,     setShowNewForm]     = useState(false);
  const [createTab,       setCreateTab]       = useState(() => localStorage.getItem(CREATE_TAB_KEY) || 'template');
  const [newTitle,        setNewTitle]        = useState('');
  const [newDescription,  setNewDescription]  = useState('');
  const [newOrientation,  setNewOrientation]  = useState('horizontal');
  const [newCategory,     setNewCategory]     = useState('');
  const [categoryError,   setCategoryError]   = useState(false);
  const [newPrivacy,      setNewPrivacy]      = useState('public');
  const [newPassword,     setNewPassword]     = useState('');
  const [passwordError,   setPasswordError]   = useState(false);
  const [mapCity,         setMapCity]         = useState('');
  const [mapCountry,      setMapCountry]      = useState('');
  const [mapCenter,       setMapCenter]       = useState(null);   // { lat, lng, zoom }
  const [mapGeoLoading,   setMapGeoLoading]   = useState(false);
  const [mapGeoError,     setMapGeoError]     = useState('');
  const [mapLocationError,setMapLocationError]= useState(false);
  const mapGeoDebRef = useRef(null);
  const [deleteConfirmId,   setDeleteConfirmId]   = useState(null);
  const [showFeedback,      setShowFeedback]      = useState(false);
  const [descImages,        setDescImages]        = useState([]);
  const [descImgUploading,  setDescImgUploading]  = useState(false);
  const [descImgError,      setDescImgError]      = useState('');
  const [searchQuery,       setSearchQuery]       = useState('');
  const [duplicateTarget,   setDuplicateTarget]   = useState(null); // { id, originalTitle }
  const [duplicateTitle,    setDuplicateTitle]    = useState('');
  const [duplicatingId,     setDuplicatingId]     = useState(null);
  const [highlightId,       setHighlightId]       = useState(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const [collections,        setCollections]        = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [showNewCollModal,   setShowNewCollModal]   = useState(false);
  const [newCollTitle,       setNewCollTitle]       = useState('');
  const [newCollDesc,        setNewCollDesc]        = useState('');
  const [newCollSaving,      setNewCollSaving]      = useState('');
  const [newCollError,       setNewCollError]       = useState('');
  const [deletingCollId,     setDeletingCollId]     = useState(null);
  const [collectionModalTl,  setCollectionModalTl]  = useState(null);

  const descImgInputRef   = useRef(null);
  const highlightTimerRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll the newly-duplicated card into view after React re-renders.
  useEffect(() => {
    if (!highlightId) return;
    document.getElementById(`tl-card-${highlightId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightId]);

  // Auto-open the create form when directed by the onboarding flow.
  useEffect(() => {
    if (!autoOpenCreate) return;
    setShowNewForm(true);
    onAutoOpenCreateDone?.();
  }, [autoOpenCreate]);

  useEffect(() => {
    if (!user?.id) return;
    setCollectionsLoading(true);
    Storage.getCollections(user.id)
      .then(data => setCollections(data.collections ?? []))
      .catch(() => {})
      .finally(() => setCollectionsLoading(false));
  }, [user?.id]);

  const openDuplicate = (tl) => {
    setDuplicateTitle(`Copy of ${tl.title}`);
    setDuplicateTarget({ id: tl.id, originalTitle: tl.title });
  };

  const handleDuplicateConfirm = async () => {
    const title    = duplicateTitle.trim() || `Copy of ${duplicateTarget.originalTitle}`;
    const sourceId = duplicateTarget.id;
    setDuplicateTarget(null);
    setDuplicatingId(sourceId);
    try {
      const newId = await onDuplicate(sourceId, title);
      clearTimeout(highlightTimerRef.current);
      setHighlightId(newId);
      highlightTimerRef.current = setTimeout(() => setHighlightId(null), 2000);
    } catch (err) {
      alert(`Failed to duplicate timeline: ${err.message}`);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDescImg = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setDescImgError('');
    setDescImgUploading(true);
    try {
      const url = await Storage.uploadImage(file, 'description-image');
      setDescImages(prev => [...prev, url]);
    } catch (err) {
      setDescImgError('Upload failed. Please try again.');
      console.error('Description image upload error:', err);
    } finally {
      setDescImgUploading(false);
    }
  };

  const runGeocode = async (city, country) => {
    const q = [city, country].filter(s => s.trim()).join(', ');
    if (!q) return null;
    setMapGeoLoading(true);
    setMapGeoError('');
    try {
      const data = await nominatimSearch(q);
      if (!data.length) {
        setMapGeoError('Location not found — check spelling.');
        setMapCenter(null);
        return null;
      }
      const center = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), zoom: 6 };
      setMapCenter(center);
      setMapGeoError('');
      return center;
    } catch (err) {
      setMapGeoError(err.status === 429 ? err.message : 'Lookup failed — check your connection.');
      setMapCenter(null);
      return null;
    } finally {
      setMapGeoLoading(false);
    }
  };

  const geocodeMapLocation = useCallback((city, country) => {
    if (mapGeoDebRef.current) clearTimeout(mapGeoDebRef.current);
    const q = [city, country].filter(s => s.trim()).join(', ');
    if (!q) { setMapCenter(null); return; }
    mapGeoDebRef.current = setTimeout(() => { runGeocode(city, country); }, 400);
  }, []);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    if (!newCategory) { setCategoryError(true); return; }
    if (newPrivacy === 'private' && !newPassword.trim()) { setPasswordError(true); return; }
    if (newOrientation === 'map' && (!mapCity.trim() || !mapCountry.trim())) { setMapLocationError(true); return; }

    // Map layout: ensure we have coords before submitting. If the debounced geocode
    // hasn't completed (or hasn't started), run it synchronously now so clicking
    // Create immediately after typing still works.
    let centerToUse = mapCenter;
    if (newOrientation === 'map' && !centerToUse) {
      if (mapGeoDebRef.current) { clearTimeout(mapGeoDebRef.current); mapGeoDebRef.current = null; }
      centerToUse = await runGeocode(mapCity, mapCountry);
      if (!centerToUse) return; // geocode failed; error message already set
    }

    setCategoryError(false);
    setPasswordError(false);
    setMapLocationError(false);
    const descImgHtml = descImages.map(url =>
      `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:6px;margin:6px auto;display:block;" />`
    ).join('');

    onCreate({
      id:          uid(),
      title:       newTitle.trim(),
      description: newDescription.trim() + descImgHtml,
      orientation: newOrientation,
      category:    newCategory || null,
      events:      [],
      colors:      { ...DEFAULT_COLORS },
      bgImage:     null,
      privacy:     newPrivacy,
      password:    newPrivacy === 'private' ? newPassword.trim() : null,
      mapCenter:   newOrientation === 'map' ? (centerToUse || null) : null,
      createdAt:   Date.now(),
      updatedAt:   Date.now(),
    });
    setNewTitle('');
    setNewDescription('');
    setNewCategory('');
    setNewPrivacy('public');
    setNewPassword('');
    setMapCity('');
    setMapCountry('');
    setMapCenter(null);
    setMapGeoError('');
    setMapLocationError(false);
    setCategoryError(false);
    setPasswordError(false);
    setDescImages([]);
    setShowNewForm(false);
  };

  const handleCreateCsv = () => {
    if (!newTitle.trim()) return;
    if (!newCategory) { setCategoryError(true); return; }
    setCategoryError(false);
    const tl = {
      id:          uid(),
      title:       newTitle.trim(),
      description: '',
      orientation: 'storyline',
      category:    newCategory || null,
      events:      [],
      colors:      { ...DEFAULT_COLORS },
      bgImage:     null,
      privacy:     'public',
      password:    null,
      mapCenter:   null,
      createdAt:   Date.now(),
      updatedAt:   Date.now(),
    };
    setNewTitle('');
    setNewCategory('');
    setCategoryError(false);
    setShowNewForm(false);
    onCreate(tl, false, { openCsvImport: true });
  };

  const sorted = [...timelines].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

  const q = searchQuery.trim().toLowerCase();
  const visible = q ? sorted.filter(tl => {
    const plainDesc = (tl.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const catLabel  = TIMELINE_CATEGORIES.find(c => c.value === tl.category)?.label ?? '';
    return (
      (tl.title || '').toLowerCase().includes(q) ||
      plainDesc.toLowerCase().includes(q) ||
      catLabel.toLowerCase().includes(q) ||
      (tl.category || '').toLowerCase().includes(q)
    );
  }) : sorted;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Top nav ── */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64,
      }}>
        <button
          onClick={onFeed}
          style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', flex: 1 }}
          aria-label="Go to feed"
        >
          <div style={{ fontSize: 22, marginRight: 4, filter: 'brightness(0) invert(1)' }} aria-hidden="true">⏱</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)', margin: 0 }}>Chronos</h1>
        </button>

        {/* Desktop nav */}
        {!isMobile && (<>
          <button
            onClick={onProfile}
            aria-label="View your profile"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'none', border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', padding: '5px 12px 5px 6px',
              cursor: 'pointer', transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
          >
            {user.avatar ? (
              <img src={user.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--accent), var(--accent3))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--on-accent)' }}>
                {(user.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
            )}
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>{user.name}</span>
          </button>
          <button
            className="btn-ghost btn-sm"
            onClick={onThemeToggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0 }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
          </button>
          <NotificationBell userId={user.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
          <button className="btn-ghost btn-sm" onClick={onFeed} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="feed" size={14} /> Feed
          </button>
          <button
            className="btn-ghost btn-sm"
            onClick={() => setShowFeedback(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title="Send feedback"
          >
            <Icon name="message" size={14} /> Feedback
          </button>
          {user.isAdmin && (
            <button className="btn-ghost btn-sm" onClick={onAdmin} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent3)' }} title="Admin Panel">
              <Icon name="shield" size={14} /> Admin
            </button>
          )}
          <button className="btn-ghost btn-sm" onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="logout" size={14} /> Sign Out
          </button>
        </>)}

        {/* Mobile hamburger */}
        {isMobile && (
          <MobileMenu>
            {(close) => <>
              <button
                onClick={() => { onFeed(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name="feed" size={15} /> Feed
              </button>
              <div style={{ padding: '4px 16px' }}>
                <NotificationBell userId={user.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
              </div>
              <button
                onClick={() => { onThemeToggle(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button
                onClick={() => { onProfile(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name="user" size={15} /> Profile
              </button>
              {user.isAdmin && (
                <button
                  onClick={() => { onAdmin(); close(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--accent3)', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Icon name="shield" size={15} /> Admin
                </button>
              )}
              <button
                onClick={() => { onLogout(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--danger)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name="logout" size={15} /> Sign Out
              </button>
            </>}
          </MobileMenu>
        )}
      </nav>

      <main className="page-main" style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px' }}>

        {/* ── Hero ── */}
        <div className="dashboard-hero" style={{ marginBottom: 48 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 32, marginBottom: 8 }}>My Dashboard</h2>
          <p style={{ color: 'var(--text2)', fontSize: 15 }}>Create, manage, and share interactive timelines.</p>
        </div>

        {/* ── PWA install nudge (shows after user creates 3rd timeline) ── */}
        {showInstallNudge && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--accent)40',
            borderRadius: 'var(--radius)', padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24,
            boxShadow: '0 2px 12px rgba(201,169,110,0.08)',
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>📱</span>
            <span style={{ flex: 1, fontSize: 14, color: 'var(--text2)' }}>
              Install Chronos on your device for the best experience
            </span>
            <button
              onClick={onInstall}
              style={{
                background: 'var(--accent)', color: '#1a1408', border: 'none',
                borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              Install
            </button>
            <button
              onClick={() => {
                localStorage.setItem('chronos_install_nudge_dismissed', '1');
                setInstallNudgeDismissed(true);
              }}
              style={{
                background: 'none', border: 'none', color: 'var(--text3)',
                cursor: 'pointer', fontSize: 13, padding: '4px 8px', flexShrink: 0,
              }}
            >
              Not Now
            </button>
          </div>
        )}

        {/* ── Search bar ── */}
        {timelines.length > 0 && (
          <div style={{ position: 'relative', marginBottom: 24 }}>
            <div style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text3)', pointerEvents: 'none', display: 'flex',
            }}>
              <Icon name="search" size={15} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by title, description, or category…"
              aria-label="Search your timelines"
              style={{ paddingLeft: 36, paddingRight: searchQuery ? 36 : 14 }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text3)',
                  cursor: 'pointer', padding: 4, display: 'flex',
                }}
              >
                <Icon name="close" size={14} />
              </button>
            )}
          </div>
        )}

        {/* ── New timeline trigger / form ── */}
        {!showNewForm ? (
          <button
            onClick={() => setShowNewForm(true)}
            aria-label="Create new timeline"
            style={{
              width: '100%', padding: 24,
              background: 'transparent',
              border: `2px dashed ${timelines.length === 0 ? 'var(--accent)' : 'var(--border2)'}`,
              borderRadius: 'var(--radius-xl)',
              cursor: 'pointer',
              color: timelines.length === 0 ? 'var(--accent)' : 'var(--text3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
              fontSize: 15, marginBottom: 32, transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = timelines.length === 0 ? 'var(--accent)' : 'var(--border2)'; e.currentTarget.style.color = timelines.length === 0 ? 'var(--accent)' : 'var(--text3)'; }}
          >
            <Icon name="plus" size={20} /> Create New Timeline
          </button>
        ) : (
          <div
            role="region"
            aria-label="New timeline form"
            style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-xl)', padding: 24, marginBottom: 32 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>New Timeline</h3>
              <button
                type="button"
                onClick={() => { setShowNewForm(false); setNewTitle(''); setNewDescription(''); setNewCategory(''); setNewPrivacy('public'); setNewPassword(''); setMapCity(''); setMapCountry(''); setMapCenter(null); setDescImages([]); setCategoryError(false); setPasswordError(false); }}
                aria-label="Close form"
                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 20, padding: 4 }}
              >✕</button>
            </div>

            {/* Tab selector */}
            <div className="tpl-modal-tabs">
              <button
                type="button"
                className={`tpl-modal-tab${createTab === 'template' ? ' active' : ''}`}
                onClick={() => { setCreateTab('template'); localStorage.setItem(CREATE_TAB_KEY, 'template'); }}
              >
                Start from template
              </button>
              <button
                type="button"
                className={`tpl-modal-tab${createTab === 'scratch' ? ' active' : ''}`}
                onClick={() => { setCreateTab('scratch'); localStorage.setItem(CREATE_TAB_KEY, 'scratch'); }}
              >
                Start from scratch
              </button>
              <button
                type="button"
                className={`tpl-modal-tab${createTab === 'csv' ? ' active' : ''}`}
                onClick={() => { setCreateTab('csv'); localStorage.setItem(CREATE_TAB_KEY, 'csv'); }}
              >
                Import CSV
              </button>
            </div>

            {/* Template browser */}
            {createTab === 'template' && (
              <TemplateBrowser
                onUseTemplate={(timeline) => {
                  setShowNewForm(false);
                  onCreate(timeline, true);
                }}
              />
            )}

            {/* Scratch form — only shown when tab is 'scratch' */}
            {createTab === 'scratch' && (
            <div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="new-tl-name">Timeline Name</label>
              <input
                id="new-tl-name"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value.slice(0, 200))}
                placeholder="e.g. My Career Journey"
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                maxLength={200}
                autoFocus
              />
              {newTitle.length > 160 && (
                <div style={{ fontSize: 11, color: newTitle.length >= 200 ? 'var(--danger)' : 'var(--text3)', textAlign: 'right', marginTop: 4 }}>
                  {newTitle.length}/200
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <legend style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, display: 'block' }}>
                  Layout
                </legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['horizontal', 'vertical', 'zigzag', 'storyline', 'newspaper', 'swimlane', 'tree', 'filmstrip', 'spine', 'map'].map(o => (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={newOrientation === o}
                      onClick={() => setNewOrientation(o)}
                      style={{
                        padding: '8px 14px', borderRadius: 'var(--radius)',
                        border: `1px solid ${newOrientation === o ? 'var(--accent)' : 'var(--border2)'}`,
                        background: newOrientation === o ? 'var(--accent)22' : 'transparent',
                        color: newOrientation === o ? 'var(--accent)' : 'var(--text2)',
                        cursor: 'pointer', fontSize: 13,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <Icon name={o} size={14} />
                      {o.charAt(0).toUpperCase() + o.slice(1)}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>

            {newOrientation === 'map' && (
              <div style={{ marginBottom: 16, padding: '14px 16px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <label style={{ marginBottom: 10, display: 'block' }}>
                  Map Starting Location
                  {mapLocationError && (
                    <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}> — required for Map layout</span>
                  )}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label htmlFor="new-tl-map-city" style={{ fontSize: 11, color: mapLocationError && !mapCity.trim() ? 'var(--danger)' : 'var(--text3)', marginBottom: 4 }}>
                      City <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <input
                      id="new-tl-map-city"
                      value={mapCity}
                      onChange={e => { setMapCity(e.target.value); setMapLocationError(false); geocodeMapLocation(e.target.value, mapCountry); }}
                      placeholder="e.g. Paris"
                      autoComplete="off"
                      aria-required="true"
                      style={{ borderColor: mapLocationError && !mapCity.trim() ? 'var(--danger)' : undefined }}
                    />
                  </div>
                  <div>
                    <label htmlFor="new-tl-map-country" style={{ fontSize: 11, color: mapLocationError && !mapCountry.trim() ? 'var(--danger)' : 'var(--text3)', marginBottom: 4 }}>
                      Country <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <input
                      id="new-tl-map-country"
                      value={mapCountry}
                      onChange={e => { setMapCountry(e.target.value); setMapLocationError(false); geocodeMapLocation(mapCity, e.target.value); }}
                      placeholder="e.g. France"
                      autoComplete="off"
                      aria-required="true"
                      style={{ borderColor: mapLocationError && !mapCountry.trim() ? 'var(--danger)' : undefined }}
                    />
                  </div>
                </div>
                {mapGeoLoading && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>Looking up location…</div>
                )}
                {!mapGeoLoading && mapCenter && !mapGeoError && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="check" size={12} />
                    {[mapCity, mapCountry].filter(Boolean).join(', ')} — {mapCenter.lat.toFixed(3)}, {mapCenter.lng.toFixed(3)}
                  </div>
                )}
                {mapGeoError && (
                  <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{mapGeoError}</div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label>Description <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
              <RichTextEditor
                value={newDescription}
                onChange={setNewDescription}
                maxLength={2000}
                placeholder="A short summary of this timeline…"
              />

              {/* ── Description images ── */}
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Description Images
                </p>

                {descImages.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {descImages.map(url => (
                      <div key={url} style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={url}
                          alt="Description image"
                          loading="lazy"
                          style={{
                            height: 80, width: 'auto', maxWidth: 150,
                            objectFit: 'cover',
                            borderRadius: 'var(--radius)',
                            border: '1px solid var(--border)',
                            display: 'block',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setDescImages(prev => prev.filter(u => u !== url))}
                          aria-label="Remove image"
                          style={{
                            position: 'absolute', top: 4, right: 4,
                            background: 'rgba(0,0,0,0.65)', border: 'none',
                            borderRadius: 4, color: '#fff',
                            fontSize: 11, padding: '2px 7px', cursor: 'pointer',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => descImgInputRef.current.click()}
                  disabled={descImgUploading}
                  aria-label="Add image to description"
                  style={{
                    width: '100%', padding: '12px 0',
                    background: 'transparent',
                    border: '2px dashed var(--border2)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--text3)',
                    fontSize: 13,
                    cursor: descImgUploading ? 'not-allowed' : 'pointer',
                    opacity: descImgUploading ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'border-color 0.2s, color 0.2s',
                  }}
                  onMouseEnter={e => { if (!descImgUploading) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
                >
                  <Icon name="image" size={15} />
                  {descImgUploading ? 'Uploading…' : 'Add Image to Description'}
                </button>
                {descImgError && (
                  <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
                    {descImgError}
                  </p>
                )}
                <input
                  ref={descImgInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleDescImg}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="new-tl-category">
                Category{' '}
                {categoryError && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}>— required</span>}
              </label>
              <div style={{ position: 'relative' }}>
                {newCategory && (() => {
                  const cat = TIMELINE_CATEGORIES.find(c => c.value === newCategory);
                  return cat ? (
                    <span aria-hidden="true" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none', zIndex: 1 }}>
                      {cat.emoji}
                    </span>
                  ) : null;
                })()}
                <select
                  id="new-tl-category"
                  value={newCategory}
                  onChange={e => { setNewCategory(e.target.value); if (e.target.value) setCategoryError(false); }}
                  aria-required="true"
                  style={{
                    width: '100%',
                    paddingLeft: newCategory ? 36 : 12,
                    paddingRight: 32, paddingTop: 10, paddingBottom: 10,
                    border: `1px solid ${categoryError ? 'var(--danger)' : 'var(--border2)'}`,
                    borderRadius: 'var(--radius)',
                    background: 'var(--surface)',
                    fontSize: 14,
                    color: newCategory ? 'var(--text)' : 'var(--text3)',
                    appearance: 'none', WebkitAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <option value="">— Select a category —</option>
                  {TIMELINE_CATEGORY_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.values.map(val => {
                        const cat = TIMELINE_CATEGORIES.find(c => c.value === val);
                        return cat ? <option key={val} value={val}>{cat.emoji} {cat.label}</option> : null;
                      })}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Privacy</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: newPrivacy === 'private' ? 10 : 0 }}>
                {[
                  { key: 'public',  label: 'Public',  icon: 'globe' },
                  { key: 'private', label: 'Private', icon: 'lock'  },
                ].map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={newPrivacy === key}
                    onClick={() => { setNewPrivacy(key); setNewPassword(''); setPasswordError(false); }}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 'var(--radius)',
                      border: `1px solid ${newPrivacy === key ? 'var(--accent)' : 'var(--border2)'}`,
                      background: newPrivacy === key ? 'var(--accent)22' : 'transparent',
                      color: newPrivacy === key ? 'var(--accent)' : 'var(--text2)',
                      cursor: 'pointer', fontSize: 13, fontWeight: newPrivacy === key ? 600 : 400,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Icon name={icon} size={14} /> {label}
                  </button>
                ))}
              </div>
              {newPrivacy === 'private' && (
                <div>
                  <label htmlFor="new-tl-password">
                    Password{' '}
                    {passwordError && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}>— required</span>}
                  </label>
                  <input
                    id="new-tl-password"
                    type="password"
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); if (e.target.value) setPasswordError(false); }}
                    placeholder="Enter a password…"
                    autoComplete="new-password"
                    style={passwordError ? { borderColor: 'var(--danger)' } : {}}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => { setShowNewForm(false); setNewPrivacy('public'); setNewPassword(''); setCategoryError(false); setPasswordError(false); }}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={descImgUploading || mapGeoLoading} aria-disabled={descImgUploading || mapGeoLoading}>
                {descImgUploading ? 'Uploading…' : mapGeoLoading ? 'Locating…' : 'Create Timeline'}
              </button>
            </div>
            </div>
            )}

            {/* CSV import tab */}
            {createTab === 'csv' && (
            <div>
              <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                Create a blank timeline, then import your CSV file in the editor. Use <strong>Layout: Storyline</strong> for narrative-style imports.
              </div>

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="csv-tl-name">Timeline Name</label>
                <input
                  id="csv-tl-name"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value.slice(0, 200))}
                  placeholder="e.g. My Career Journey"
                  onKeyDown={e => e.key === 'Enter' && handleCreateCsv()}
                  maxLength={200}
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label>Category{categoryError && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}> — required</span>}</label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={newCategory}
                    onChange={e => { setNewCategory(e.target.value); if (e.target.value) setCategoryError(false); }}
                    style={{
                      width: '100%',
                      paddingLeft: newCategory ? 36 : 12,
                      paddingRight: 32, paddingTop: 10, paddingBottom: 10,
                      border: `1px solid ${categoryError ? 'var(--danger)' : 'var(--border2)'}`,
                      borderRadius: 'var(--radius)',
                      background: 'var(--surface)',
                      fontSize: 14,
                      color: newCategory ? 'var(--text)' : 'var(--text3)',
                      appearance: 'none', WebkitAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">— Select a category —</option>
                    {TIMELINE_CATEGORY_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.values.map(val => {
                          const cat = TIMELINE_CATEGORIES.find(c => c.value === val);
                          return cat ? <option key={val} value={val}>{cat.emoji} {cat.label}</option> : null;
                        })}
                      </optgroup>
                    ))}
                  </select>
                  {newCategory && (
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none' }}>
                      {TIMELINE_CATEGORIES.find(c => c.value === newCategory)?.emoji}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn-ghost" onClick={() => { setShowNewForm(false); setNewTitle(''); setNewCategory(''); setCategoryError(false); }}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateCsv}>
                  Create &amp; Import CSV
                </button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* ── Empty states ── */}
        {timelines.length === 0 && !showNewForm && (
          <div className="dashboard-empty-state">
            <div className="dashboard-empty-illustration" aria-hidden="true">
              <div className="dash-empty-line" />
              {['📅', '⭐', '🚀'].map((icon, i) => (
                <div key={i} className="dash-empty-node" style={{ animationDelay: `${i * 0.2}s` }}>
                  {icon}
                </div>
              ))}
            </div>
            <h2 className="dashboard-empty-headline">You haven't created any timelines yet</h2>
            <p className="dashboard-empty-desc">Pick a layout and start telling your story.</p>
            <button className="btn-primary" onClick={() => setShowNewForm(true)}>
              Create Your First Timeline
            </button>
          </div>
        )}
        {timelines.length > 0 && visible.length === 0 && searchQuery && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">🔍</div>
            <p style={{ fontSize: 15, marginBottom: 16 }}>No timelines match <strong style={{ color: 'var(--text2)' }}>"{searchQuery}"</strong></p>
            <button className="btn-ghost btn-sm" onClick={() => setSearchQuery('')}>Clear search</button>
          </div>
        )}

        {/* ── Timelines grid ── */}
        <div
          className="dashboard-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}
        >
          {visible.map(tl => {
            const colors   = tl.colors || DEFAULT_COLORS;
            const evSorted = [...tl.events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
            const minDate  = evSorted.length ? evSorted[0].startDate : null;
            const maxDate  = evSorted.length ? evSorted[evSorted.length - 1].endDate : null;

            const isHighlighted = highlightId === tl.id;
            const isDuplicating = duplicatingId === tl.id;

            return (
              <article
                key={tl.id}
                id={`tl-card-${tl.id}`}
                aria-label={tl.title}
                style={{
                  background: 'var(--surface)',
                  border: `1px solid ${isHighlighted ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  position: 'relative',
                  boxShadow: isHighlighted ? '0 0 0 3px rgba(201,169,110,0.18)' : 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = colors.secondary}
                onMouseLeave={e => e.currentTarget.style.borderColor = isHighlighted ? 'var(--accent)' : 'var(--border)'}
              >
                {/* Background image preview — falls back to color strip */}
                {getTimelinePreviewImage(tl) ? (
                  <div style={{ height: 160, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                    <img
                      src={getTimelinePreviewImage(tl)}
                      alt=""
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72,
                      background: 'linear-gradient(to bottom, transparent, var(--surface))' }} aria-hidden="true" />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }} aria-hidden="true" />
                  </div>
                ) : (
                  <div style={{ height: 8, background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }} aria-hidden="true" />
                )}

                <div style={{ padding: '18px 20px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: tl.category ? 8 : 12 }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, flex: 1, marginRight: 8 }}>{tl.title}</h3>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {tl.privacy === 'private' && (
                        <span style={{ fontSize: 10, background: 'var(--bg3)', borderRadius: 6, padding: '3px 8px', color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Icon name="lock" size={10} /> Private
                        </span>
                      )}
                      <span style={{ fontSize: 10, background: 'var(--bg3)', borderRadius: 6, padding: '3px 8px', color: 'var(--text3)' }}>
                        {tl.orientation}
                      </span>
                    </div>
                  </div>

                  {tl.category && (() => {
                    const cat = TIMELINE_CATEGORIES.find(c => c.value === tl.category);
                    return cat ? (
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 6, padding: '3px 9px', color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span aria-hidden="true">{cat.emoji}</span> {cat.label}
                        </span>
                      </div>
                    ) : null;
                  })()}

                  {hasVisibleContent(tl.description) && (
                    <div
                      dangerouslySetInnerHTML={{ __html: stripImgTags(tl.description) }}
                      style={{
                        fontSize: 13, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.5,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}
                    />
                  )}

                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: (tl.collaborators?.length > 0) ? 10 : 16 }}>
                    {tl.events.length === 0
                      ? 'No events yet'
                      : `${tl.events.length} event${tl.events.length !== 1 ? 's' : ''}`}
                    {tl.orientation === 'storyline' && Array.isArray(tl.chapters) && tl.chapters.length > 0 && (
                      <> · {tl.chapters.length} chapter{tl.chapters.length !== 1 ? 's' : ''}</>
                    )}
                    {minDate && ` · ${formatDate(minDate)} → ${formatDate(maxDate)}`}
                  </div>

                  {/* Collaborator avatars */}
                  {tl.collaborators?.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                      <div style={{ display: 'flex' }}>
                        {tl.collaborators.slice(0, 5).map((c, i) => (
                          c.avatar
                            ? <img
                                key={c.id}
                                src={c.avatar}
                                alt={c.name}
                                title={c.name}
                                style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--surface)', marginLeft: i === 0 ? 0 : -6 }}
                              />
                            : <div
                                key={c.id}
                                title={c.name}
                                style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)44', border: '2px solid var(--surface)', marginLeft: i === 0 ? 0 : -6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent)' }}
                              >
                                {c.name[0]}
                              </div>
                        ))}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {tl.collaborators.length === 1
                          ? tl.collaborators[0].name
                          : `${tl.collaborators.length} collaborators`}
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => onView(tl)}
                      className="btn-ghost btn-sm"
                      aria-label={`View ${tl.title}`}
                      disabled={isDuplicating}
                      style={{ flex: '1 1 80px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <Icon name="eye" size={14} /> View
                    </button>
                    <button
                      onClick={() => onEdit(tl)}
                      className="btn-primary btn-sm"
                      aria-label={`Edit ${tl.title}`}
                      disabled={isDuplicating}
                      style={{ flex: '1 1 80px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <Icon name="edit" size={14} /> Edit
                    </button>
                    {tl.privacy === 'public' && (
                      <button
                        onClick={() => setCollectionModalTl(tl)}
                        aria-label={`Add ${tl.title} to collection`}
                        disabled={isDuplicating}
                        title="Add to collection"
                        style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--text2)', cursor: isDuplicating ? 'not-allowed' : 'pointer', opacity: isDuplicating ? 0.5 : 1, flexShrink: 0 }}
                      >
                        <Icon name="layers" size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => openDuplicate(tl)}
                      aria-label={`Duplicate ${tl.title}`}
                      disabled={isDuplicating}
                      title="Duplicate"
                      style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--accent)', cursor: isDuplicating ? 'not-allowed' : 'pointer', opacity: isDuplicating ? 0.5 : 1, flexShrink: 0 }}
                    >
                      <Icon name="duplicate" size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(tl.id)}
                      aria-label={`Delete ${tl.title}`}
                      disabled={isDuplicating}
                      title="Delete"
                      style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--danger)', cursor: isDuplicating ? 'not-allowed' : 'pointer', opacity: isDuplicating ? 0.5 : 1, flexShrink: 0 }}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                </div>

                {/* Loading overlay while this card is being duplicated */}
                {isDuplicating && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(var(--surface-rgb, 20,17,14), 0.75)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 'var(--radius-xl)',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--text3)' }}>Duplicating…</span>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* ── Shared With Me ── */}
        {sharedTimelines.length > 0 && (
          <section style={{ marginTop: 56 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Icon name="users" size={18} />
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>Shared With Me</h2>
            </div>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 24 }}>
              Timelines other users have invited you to collaborate on.
            </p>

            <div
              className="dashboard-grid"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}
            >
              {sharedTimelines.map(tl => {
                const colors   = tl.colors || DEFAULT_COLORS;
                const evSorted = [...(tl.events || [])].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
                const minDate  = evSorted.length ? evSorted[0].startDate : null;
                const maxDate  = evSorted.length ? evSorted[evSorted.length - 1].endDate : null;
                const isEditor = tl.collaboratorRole === 'editor';

                return (
                  <article
                    key={tl.id}
                    aria-label={tl.title}
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                      transition: 'border-color 0.2s',
                      position: 'relative',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = colors.secondary}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    {/* Color strip */}
                    <div style={{ height: 8, background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }} aria-hidden="true" />

                    <div style={{ padding: '18px 20px 20px' }}>
                      {/* Owner attribution */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        {tl.owner?.avatar
                          ? <img src={tl.owner.avatar} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />
                          : <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                              {(tl.owner?.name || '?')[0]}
                            </div>
                        }
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>by {tl.owner?.name}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                          background: isEditor ? 'var(--accent)22' : 'var(--bg3)',
                          color:      isEditor ? 'var(--accent)'   : 'var(--text3)',
                          border:    `1px solid ${isEditor ? 'var(--accent)40' : 'var(--border)'}`,
                        }}>
                          {isEditor ? 'Editor' : 'Viewer'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: tl.category ? 8 : 12 }}>
                        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, flex: 1, marginRight: 8 }}>{tl.title}</h3>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                          {/* View count — owner's own dashboard; suppressed
                              on draft timelines so the Draft badge takes
                              priority per spec. */}
                          {tl.privacy !== 'draft' && (tl.viewCount ?? 0) > 0 && (
                            <span
                              title={`${(tl.viewCount ?? 0).toLocaleString()} views`}
                              style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <Icon name="eye" size={10} /> {formatCompactCount(tl.viewCount ?? 0)}
                            </span>
                          )}
                          {tl.privacy === 'draft' && (
                            <span style={{
                              fontSize: 10,
                              background: 'rgba(201, 169, 110, 0.18)',
                              border: '1px solid rgba(201, 169, 110, 0.45)',
                              borderRadius: 6, padding: '3px 8px',
                              color: 'var(--accent, #C9A96E)', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              <Icon name="edit" size={10} /> Draft
                            </span>
                          )}
                          {tl.privacy === 'private' && (
                            <span style={{ fontSize: 10, background: 'var(--bg3)', borderRadius: 6, padding: '3px 8px', color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Icon name="lock" size={10} /> Private
                            </span>
                          )}
                          <span style={{ fontSize: 10, background: 'var(--bg3)', borderRadius: 6, padding: '3px 8px', color: 'var(--text3)' }}>
                            {tl.orientation}
                          </span>
                        </div>
                      </div>

                      {tl.category && (() => {
                        const cat = TIMELINE_CATEGORIES.find(c => c.value === tl.category);
                        return cat ? (
                          <div style={{ marginBottom: 10 }}>
                            <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 6, padding: '3px 9px', color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span aria-hidden="true">{cat.emoji}</span> {cat.label}
                            </span>
                          </div>
                        ) : null;
                      })()}

                      {hasVisibleContent(tl.description) && (
                        <div
                          dangerouslySetInnerHTML={{ __html: stripImgTags(tl.description) }}
                          style={{
                            fontSize: 13, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.5,
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}
                        />
                      )}

                      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                        {(tl.events || []).length === 0
                          ? 'No events yet'
                          : `${tl.events.length} event${tl.events.length !== 1 ? 's' : ''}`}
                        {tl.orientation === 'storyline' && Array.isArray(tl.chapters) && tl.chapters.length > 0 && (
                          <> · {tl.chapters.length} chapter{tl.chapters.length !== 1 ? 's' : ''}</>
                        )}
                        {minDate && ` · ${formatDate(minDate)} → ${formatDate(maxDate)}`}
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => onView(tl)}
                          className="btn-ghost btn-sm"
                          aria-label={`View ${tl.title}`}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          <Icon name="eye" size={14} /> View
                        </button>
                        {isEditor && (
                          <button
                            onClick={() => onEdit(tl)}
                            className="btn-primary btn-sm"
                            aria-label={`Edit ${tl.title}`}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                          >
                            <Icon name="edit" size={14} /> Edit
                          </button>
                        )}
                        {tl.privacy === 'public' && (
                          <button
                            onClick={() => setCollectionModalTl(tl)}
                            aria-label={`Add ${tl.title} to collection`}
                            title="Add to collection"
                            style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--text2)', cursor: 'pointer' }}
                          >
                            <Icon name="layers" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Collections ── */}
        <section style={{ marginTop: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon name="layers" size={18} />
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>Collections</h2>
            </div>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => { setNewCollTitle(''); setNewCollDesc(''); setNewCollError(''); setShowNewCollModal(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Icon name="plus" size={13} /> New Collection
            </button>
          </div>
          <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 24 }}>
            Group your public timelines into named collections that others can browse.
          </p>

          {collectionsLoading ? (
            <p style={{ fontSize: 14, color: 'var(--text3)', textAlign: 'center', padding: '48px 0' }}>Loading…</p>
          ) : collections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text3)' }}>
              <div style={{ opacity: 0.3, display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <Icon name="layers" size={40} />
              </div>
              <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>No collections yet</p>
              <p style={{ fontSize: 13 }}>Create a collection to group your public timelines.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {collections.map(coll => (
                <div key={coll.id} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-xl)', padding: '18px 20px',
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  transition: 'border-color 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a
                      href={`/collections/${coll.id}`}
                      style={{ fontWeight: 600, fontSize: 15, color: 'var(--accent)', textDecoration: 'none', display: 'block', marginBottom: 4 }}
                    >
                      {coll.title}
                    </a>
                    {coll.description && (
                      <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 6px', lineHeight: 1.4 }}>{coll.description}</p>
                    )}
                    <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
                      {coll.timelineCount} timeline{coll.timelineCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    disabled={deletingCollId === coll.id}
                    onClick={async () => {
                      if (!window.confirm(`Delete "${coll.title}"? This cannot be undone.`)) return;
                      setDeletingCollId(coll.id);
                      try {
                        await Storage.deleteCollection(coll.id);
                        setCollections(prev => prev.filter(c => c.id !== coll.id));
                      } catch {}
                      setDeletingCollId(null);
                    }}
                    style={{ color: 'var(--danger)', borderColor: 'rgba(201,88,88,0.3)', flexShrink: 0, opacity: deletingCollId === coll.id ? 0.5 : 1 }}
                  >
                    {deletingCollId === coll.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* ── New collection modal ── */}
      <Modal open={showNewCollModal} onClose={() => setShowNewCollModal(false)} title="New Collection" width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="dash-new-coll-title">Title</label>
            <input
              id="dash-new-coll-title"
              value={newCollTitle}
              onChange={e => { setNewCollTitle(e.target.value.slice(0, 200)); setNewCollError(''); }}
              placeholder="My Collection"
              maxLength={200}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="dash-new-coll-desc">Description <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              id="dash-new-coll-desc"
              value={newCollDesc}
              onChange={e => setNewCollDesc(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="What is this collection about?"
              maxLength={500}
              style={{ resize: 'vertical' }}
            />
          </div>
          {newCollError && (
            <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{newCollError}</p>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={() => setShowNewCollModal(false)} disabled={!!newCollSaving}>Cancel</button>
            <button
              className="btn-primary"
              disabled={!!newCollSaving || !newCollTitle.trim()}
              style={{ opacity: !newCollTitle.trim() ? 0.5 : 1 }}
              onClick={async () => {
                if (!newCollTitle.trim()) { setNewCollError('Title is required.'); return; }
                setNewCollSaving('saving');
                try {
                  const { collection } = await Storage.createCollection({ title: newCollTitle.trim(), description: newCollDesc.trim() || undefined });
                  setCollections(prev => [collection, ...prev]);
                  setShowNewCollModal(false);
                } catch (err) {
                  setNewCollError(err.message || 'Failed to create collection.');
                }
                setNewCollSaving('');
              }}
            >
              {newCollSaving ? 'Creating…' : 'Create Collection'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete confirmation modal ── */}
      <AddToCollectionModal
        open={!!collectionModalTl}
        onClose={() => setCollectionModalTl(null)}
        timelineId={collectionModalTl?.id}
        timelineName={collectionModalTl?.title}
        currentUser={user}
      />

      <Modal open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Timeline?" width={400}>
        <p style={{ color: 'var(--text2)', marginBottom: 24 }}>
          This action cannot be undone. All events in this timeline will be permanently deleted.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
          <button className="btn-danger" onClick={() => { onDelete(deleteConfirmId); setDeleteConfirmId(null); }}>
            Delete Timeline
          </button>
        </div>
      </Modal>

      <FeedbackModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        currentUser={user}
      />

      {/* ── Duplicate confirmation modal ── */}
      <Modal open={!!duplicateTarget} onClose={() => setDuplicateTarget(null)} title="Duplicate Timeline" width={420}>
        {duplicateTarget && (
          <div>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 16 }}>
              Enter a name for the new timeline. It will be set to <strong style={{ color: 'var(--text)' }}>Public</strong> and start with no social data.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="dup-tl-title">Timeline Name</label>
              <input
                id="dup-tl-title"
                value={duplicateTitle}
                onChange={e => setDuplicateTitle(e.target.value.slice(0, 200))}
                placeholder={`Copy of ${duplicateTarget.originalTitle}`}
                onKeyDown={e => e.key === 'Enter' && handleDuplicateConfirm()}
                maxLength={200}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setDuplicateTarget(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleDuplicateConfirm}>
                <Icon name="duplicate" size={14} style={{ marginRight: 6 }} /> Duplicate
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '32px 24px 24px', marginTop: 40, borderTop: '1px solid var(--border)' }}>
        <p style={{ fontSize: 12, color: 'var(--text3)' }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Terms of Service</a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Privacy Policy</a>
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;
